import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';
import { ProgressBarModule } from 'primeng/progressbar';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { forkJoin } from 'rxjs';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingDestruction, WarehousingItem } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-destruction-view',
  standalone: true,
  imports: [CommonModule, RouterModule, BreadcrumbModule, ButtonModule, ImageModule, ProgressBarModule, TagModule, ToastModule, PageDetailHeroComponent, PageFormSectionComponent, PageMetricStripComponent, PageShellComponent],
  providers: [MessageService],
  templateUrl: './warehousing-destruction-view.component.html'
})
export class WarehousingDestructionViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private warehousingService = inject(WarehousingService);
  private permissionService = inject(PermissionService);
  private messageService = inject(MessageService);

  isLoading = false;
  isSubmitting = false;
  destruction?: WarehousingDestruction;
  items: WarehousingItem[] = [];
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Destruction Details', 'Destruction', '/warehousing/destruction');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();

  ngOnInit(): void {
    this.loadDestruction();
  }

  loadDestruction(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.isLoading = true;
    forkJoin({
      destruction: this.warehousingService.getDestruction(id),
      items: this.warehousingService.getItems()
    }).subscribe({
      next: response => {
        this.destruction = response.destruction.data;
        this.items = response.items.data || [];
        this.breadcrumbItems = warehousingBreadcrumb(this.destruction?.destructionNumber || 'Destruction Details', 'Destruction', '/warehousing/destruction');
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  itemName(id?: number): string {
    const item = this.items.find(row => row.id === id);
    return itemIdentityLabel(item);
  }

  supplierName(destruction?: WarehousingDestruction): string {
    if (!destruction) {
      return '-';
    }
    return destruction.supplierClientName || destruction.vendorName || 'Internal team';
  }

  evidenceUrls(destruction?: WarehousingDestruction): string[] {
    return `${destruction?.evidenceUrls || ''}`
      .split('\n')
      .map(row => row.trim())
      .filter(Boolean);
  }

  evidenceFileName(url: string): string {
    const clean = (url || '').split('?')[0];
    return decodeURIComponent(clean.substring(clean.lastIndexOf('/') + 1));
  }

  isImageUrl(url: string): boolean {
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((url || '').split('?')[0]);
  }

  canConfirm(): boolean {
    return this.isDraft() && this.permissionService.hasPermission(app_permissions.warehousing.destruction.confirm);
  }

  canCancel(): boolean {
    return this.isDraft() && this.permissionService.hasPermission(app_permissions.warehousing.destruction.cancel);
  }

  confirmDestruction(): void {
    if (!this.destruction?.id || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.warehousingService.confirmDestruction(this.destruction.id).subscribe({
      next: response => {
        this.destruction = response.data || this.destruction;
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Destruction Confirmed', detail: 'Stock quantity was written off and the destruction ledger was updated.' });
      },
      error: () => this.isSubmitting = false
    });
  }

  cancelDestruction(): void {
    if (!this.destruction?.id || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    this.warehousingService.cancelDestruction(this.destruction.id).subscribe({
      next: response => {
        this.destruction = response.data || this.destruction;
        this.isSubmitting = false;
        this.messageService.add({ severity: 'success', summary: 'Draft Cancelled', detail: 'The destruction draft was cancelled without changing stock.' });
      },
      error: () => this.isSubmitting = false
    });
  }

  metrics(): PageMetric[] {
    return [
      { label: 'Status', value: this.destruction?.status || '-', tone: this.isDraft() ? 'warn' : this.destruction?.status === 'CONFIRMED' ? 'success' : 'danger' },
      { label: 'Source', value: this.destruction?.sourceType || '-' },
      { label: 'Quantity', value: this.formatAmount(this.destruction?.quantityDestroyed, 4), tone: 'danger' },
      { label: 'Estimated Cost', value: this.formatAmount(this.destruction?.estimatedCost), tone: 'info' },
      { label: 'Actual Cost', value: this.formatAmount(this.destruction?.actualCost), tone: 'info' }
    ];
  }

  statusSeverity = statusSeverity;

  private isDraft(): boolean {
    return `${this.destruction?.status || ''}`.toUpperCase() === 'DRAFT';
  }

  private formatAmount(value?: number, maxFractionDigits = 2): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: maxFractionDigits, minimumFractionDigits: Math.min(2, maxFractionDigits) }).format(value || 0);
  }
}
